import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Page, PageHeader, StatusBadge, Empty } from "@/components/shared";
import { SIGN_STATUS, CUSTOMER_STATUS, fmtNOK, fmtDate } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileSignature, Upload, Plus, X, Send, Download, CheckCircle2, Clock } from "lucide-react";

const SR_STATUS = {
  sendt: { label: "Venter signering", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  delvis_signert: { label: "Delvis signert", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  fullfort: { label: "Fullført", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
};

function UploadForSigningDialog({ customers, onDone }) {
  const [open, setOpen] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [contacts, setContacts] = useState([]);
  const [picked, setPicked] = useState(() => new Set());
  const [manual, setManual] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setCustomerId(""); setContacts([]); setPicked(new Set()); setManual([]); setTitle(""); setFile(null); }
  }, [open]);

  useEffect(() => {
    if (!customerId) { setContacts([]); setPicked(new Set()); return; }
    api.get(`/customers/${customerId}`).then((r) => {
      const cts = (r.data.contacts || []).filter((c) => c.email);
      setContacts(cts);
      setPicked(new Set(cts.filter((c) => c.signatory).map((c) => c.id)));
    }).catch(() => { setContacts([]); setPicked(new Set()); });
  }, [customerId]);

  const toggle = (id) => setPicked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const addManual = () => setManual((m) => [...m, { name: "", email: "" }]);
  const setManualField = (i, k, v) => setManual((m) => m.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const removeManual = (i) => setManual((m) => m.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!customerId) { toast.error("Velg en kunde"); return; }
    if (!file) { toast.error("Velg en PDF-fil"); return; }
    const signers = [
      ...contacts.filter((c) => picked.has(c.id)).map((c) => ({ name: c.name, email: c.email, contact_id: c.id })),
      ...manual.filter((m) => m.email.trim()).map((m) => ({ name: m.name.trim(), email: m.email.trim() })),
    ];
    if (signers.length === 0) { toast.error("Legg til minst én signatar med e-post"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title.trim());
      fd.append("customer_id", customerId);
      fd.append("signers", JSON.stringify(signers));
      await api.post("/sign-requests", fd);
      toast.success("Dokument sendt til signering");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="sign-upload-open" className="bg-white text-black hover:bg-white/90"><Upload className="w-4 h-4 mr-1" /> Last opp dokument</Button>
      </DialogTrigger>
      <DialogContent className="bg-popover border-border max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Last opp dokument til signering</DialogTitle>
          <DialogDescription>Velg kunde, last opp en PDF og send den til digital signering med engangskode.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-caps">Kunde</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="sign-upload-customer"><SelectValue placeholder="Velg kunde…" /></SelectTrigger>
              <SelectContent className="bg-popover border-border max-h-64">
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Tittel</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 bg-secondary border-border" data-testid="sign-upload-title" placeholder="F.eks. Fullmakt, NDA, tilleggsavtale…" />
          </div>
          <div>
            <Label className="label-caps">PDF-fil</Label>
            <Input type="file" accept=".pdf,application/pdf" onChange={(e) => { const f = e.target.files[0]; setFile(f); if (f && !title.trim()) setTitle(f.name.replace(/\.pdf$/i, "")); }} className="mt-1 bg-secondary border-border" data-testid="sign-upload-file" />
          </div>
          <div>
            <Label className="label-caps">Signatarer</Label>
            {!customerId ? (
              <p className="text-xs text-muted-foreground mt-1">Velg en kunde for å hente kontakter.</p>
            ) : contacts.length === 0 ? (
              <p className="text-xs text-muted-foreground mt-1">Ingen kontakter med e-post på denne kunden. Legg til signatar manuelt under.</p>
            ) : (
              <div className="mt-2 space-y-2">
                {contacts.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={picked.has(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`sign-contact-${c.id}`} />
                    <span>{c.name} <span className="text-muted-foreground">· {c.email}</span>{c.signatory && <span className="text-emerald-400 text-xs ml-1">signaturberettiget</span>}</span>
                  </label>
                ))}
              </div>
            )}
            {manual.map((m, i) => (
              <div key={i} className="flex items-center gap-2 mt-2">
                <Input value={m.name} onChange={(e) => setManualField(i, "name", e.target.value)} placeholder="Navn" className="bg-secondary border-border" data-testid={`sign-manual-name-${i}`} />
                <Input value={m.email} onChange={(e) => setManualField(i, "email", e.target.value)} placeholder="E-post" className="bg-secondary border-border" data-testid={`sign-manual-email-${i}`} />
                <button type="button" onClick={() => removeManual(i)} className="text-muted-foreground hover:text-foreground p-1" aria-label="Fjern"><X className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" onClick={addManual} className="mt-2 text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="sign-add-manual"><Plus className="w-3.5 h-3.5" /> Legg til signatar manuelt</button>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="sign-upload-submit" className="bg-white text-black hover:bg-white/90">
            {busy ? "Sender…" : "Send til signering"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignRequestsBox({ customers }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/sign-requests").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const resend = async (id) => {
    try { const r = await api.post(`/sign-requests/${id}/resend`); toast.success(`Påminnelse sendt til ${r.data.sent_to.join(", ") || "ingen"}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const download = async (id) => {
    try {
      const res = await api.get(`/sign-requests/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-xl">Dokumenter til signering</h2>
          <p className="text-muted-foreground text-sm mt-0.5">Last opp en PDF og send den til digital signering</p>
        </div>
        <UploadForSigningDialog customers={customers} onDone={load} />
      </div>

      {rows.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-xl">
          <Empty icon={Upload} title="Ingen dokumenter til signering" hint="Trykk «Last opp dokument» for å sende en PDF til digital signering." />
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left">{["Dokument", "Kunde", "Signatarer", "Status", "Opprettet", ""].map((h) => <th key={h} className="px-4 py-3 label-caps">{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`sign-request-row-${r.id}`} className="border-b border-border/50 hover:bg-accent/30">
                  <td className="px-4 py-3 font-medium flex items-center gap-2"><FileSignature className="w-4 h-4 text-muted-foreground" /> {r.title}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.customer_name}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                      {r.status === "fullfort" ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Clock className="w-4 h-4" />}
                      {r.signers_signed}/{r.signers_total}
                    </span>
                  </td>
                  <td className="px-4 py-3"><StatusBadge map={SR_STATUS} value={r.status} /></td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{fmtDate(r.created_at)}<div className="text-muted-foreground/70">{r.created_by}</div></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      {r.status !== "fullfort" && (
                        <button onClick={() => resend(r.id)} data-testid={`sign-request-resend-${r.id}`} title="Send påminnelse" className="text-muted-foreground hover:text-foreground p-1"><Send className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => download(r.id)} data-testid={`sign-request-download-${r.id}`} title={r.status === "fullfort" ? "Last ned signert" : "Last ned original"} className="text-muted-foreground hover:text-foreground p-1"><Download className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Agreements() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/customers").then((r) => {
      setCustomers([...r.data].sort((a, b) => (a.legal_name || "").localeCompare(b.legal_name || "", "nb")));
      setRows(r.data.filter((c) => (c.sign_status && c.sign_status !== "ikke_opprettet") || c.agreement_status !== "ingen"));
    });
  }, []);
  return (
    <Page>
      <PageHeader title="Avtaler" subtitle="Oppdragsavtaler og signeringsstatus" testid="agreements-header" />
      {rows.length === 0 ? <Empty icon={FileSignature} title="Ingen avtaler" hint="Generer en avtale fra et kundekort." /> : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left">{["Kunde", "Kundestatus", "Avtalestatus", "Signering", "MRR"].map((h) => <th key={h} className="px-4 py-3 label-caps">{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} data-testid={`agreement-row-${c.id}`} onClick={() => nav(`/kunder/${c.id}`)} className="border-b border-border/50 hover:bg-accent/50 cursor-pointer">
                  <td className="px-4 py-3 font-medium">{c.legal_name}</td>
                  <td className="px-4 py-3"><StatusBadge map={CUSTOMER_STATUS} value={c.status} /></td>
                  <td className="px-4 py-3 text-muted-foreground">{c.agreement_status}</td>
                  <td className="px-4 py-3"><StatusBadge map={SIGN_STATUS} value={c.sign_status} /></td>
                  <td className="px-4 py-3">{fmtNOK(c.mrr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SignRequestsBox customers={customers} />
    </Page>
  );
}
